// Life-event radar on Today (owner-approved idea 34): what is coming in the clients' lives in the next weeks, from data
// already in the book — milestone birthdays, loans ending, policies maturing, children turning 18, baptism anniversaries,
// the yearly «ημέρα διαφάνειας» (transparency day: the anniversary of the relationship, when the yearly transparency
// summary is due) and risk profiles that no longer fit the time to the goal. Each item explains itself and offers one
// move (a task with a fixed id, so it is never created twice).
import type { Changeset, WorkspaceView } from './changeset.ts';
import { DomainError, created, put, type Ctx } from './cmd.ts';
import { addDays, addMonths, ageOn, diffDays, type Day } from './dates.ts';
import { stableId } from './ids.ts';
import { isActivePolicy } from './rules.ts';
import { familyReminders, nextOccurrence } from './family.ts';
import { riskMismatches } from './risk-profile.ts';

export const RADAR_KINDS = ['milestone_birthday', 'loan_ends', 'policy_matures', 'turns18', 'baptism', 'transparency_day', 'risk_review'] as const;
export type RadarKind = typeof RADAR_KINDS[number];
export const MILESTONE_AGES = [18, 25, 30, 40, 50, 60, 65, 70, 75, 80, 85, 90];
export interface RadarItem {
  key: string; kind: RadarKind; personId: string; date: Day; days: number;
  /** values for the item's sentence (name of a relative, age, the loan's or policy's label, years) */
  params: Record<string, string | number>;
  refTable?: string; refId?: string;
}

/** when the yearly transparency summary of a client is due: the anniversary of the relationship (client since, or the
 *  first policy's start) — the next one from today */
export function transparencyDay(v: WorkspaceView, personId: string, today: Day): Day | null {
  const p = v.get('persons', personId);
  if (!p) return null;
  const starts = v.where('policies', 'personId', personId).map((x) => x.startDate ?? x.issueDate).filter((d): d is string => !!d).sort();
  const anchor = p.clientSince ?? starts[0] ?? null;
  return anchor ? nextOccurrence(anchor, today) : null;
}
/** the last transparency summary prepared for a client (date), if any */
export function lastTransparency(v: WorkspaceView, personId: string): Day | null {
  return v.where('client_summaries', 'personId', personId).filter((s) => s.kind === 'transparency').map((s) => s.date).sort().pop() ?? null;
}

export function lifeEventRadar(v: WorkspaceView, today: Day, horizonDays = 60): RadarItem[] {
  const end = addDays(today, horizonDays);
  const items: RadarItem[] = [];
  const live = v.rows('persons').filter((p) => !p.archivedAt && (p.stage === 'client' || p.stage === 'prospect'));
  for (const p of live) {
    if (p.birthDate) {
      const next = nextOccurrence(p.birthDate, today);
      const age = ageOn(p.birthDate, next);
      if (next <= end && MILESTONE_AGES.includes(age)) items.push({ key: `bday:${p.id}:${next}`, kind: 'milestone_birthday', personId: p.id, date: next, days: diffDays(today, next), params: { age } });
    }
    if (p.stage === 'client' && v.where('policies', 'personId', p.id).some(isActivePolicy)) {
      const td = transparencyDay(v, p.id, today);
      const last = lastTransparency(v, p.id);
      if (td && td <= end && (!last || diffDays(last, td) > 300)) items.push({ key: `transp:${p.id}:${td}`, kind: 'transparency_day', personId: p.id, date: td, days: diffDays(today, td), params: { years: Math.max(1, Number(td.slice(0, 4)) - Number((p.clientSince ?? td).slice(0, 4))) } });
    }
  }
  const alive = new Set(live.map((p) => p.id));
  for (const f of v.rows('financial_items')) {
    if (f.kind !== 'liability' || !alive.has(f.personId)) continue;
    const endDate = f.endDate ?? (f.startDate && f.termMonths ? addMonths(f.startDate, f.termMonths) : null);
    if (endDate && endDate >= today && endDate <= end) items.push({ key: `loan:${f.id}`, kind: 'loan_ends', personId: f.personId, date: endDate, days: diffDays(today, endDate), params: { label: f.label ?? f.type, lender: f.lender ?? '' }, refTable: 'financial_items', refId: f.id });
  }
  for (const pol of v.rows('policies')) {
    if (!isActivePolicy(pol) || !pol.endDate || !alive.has(pol.personId)) continue;
    if (pol.endDate >= today && pol.endDate <= end) items.push({ key: `mat:${pol.id}`, kind: 'policy_matures', personId: pol.personId, date: pol.endDate, days: diffDays(today, pol.endDate), params: { label: pol.productName }, refTable: 'policies', refId: pol.id });
  }
  for (const r of familyReminders(v, today, horizonDays)) if (alive.has(r.personId)) items.push({ key: `${r.kind}:${r.relativeId ?? r.name}:${r.date}`, kind: r.kind, personId: r.personId, date: r.date, days: r.days, params: { name: r.name, years: r.years } });
  for (const m of riskMismatches(v, today)) if (m.severity !== 'low' && alive.has(m.personId)) items.push({ key: `risk:${m.profileId}:${m.kind}`, kind: 'risk_review', personId: m.personId, date: today, days: 0, params: { level: m.level, fits: m.fits ?? '', years: m.years ?? 0, why: m.kind }, refTable: 'risk_profiles', refId: m.profileId });
  return items.sort((a, b) => a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind));
}

export const radarTaskId = (item: Pick<RadarItem, 'key'>) => stableId('life-radar', item.key);
/** a task for a radar item (a fixed id: once only), due a few days before the date */
export function radarTask(v: WorkspaceView, item: RadarItem, title: string, ctx: Ctx): Changeset {
  const id = radarTaskId(item);
  if (v.get('tasks', id)) throw new DomainError('radar.task_exists');
  const due = item.days > 7 ? addDays(item.date, -7) : ctx.today;
  const kind = item.kind === 'transparency_day' || item.kind === 'risk_review' ? 'review' as const : item.kind === 'milestone_birthday' || item.kind === 'baptism' ? 'call' as const : 'followup' as const;
  const task = created<'tasks'>(ctx, { id, kind, title, personId: item.personId, dueDate: due < ctx.today ? ctx.today : due, status: 'open', priority: item.kind === 'risk_review' ? 'high' : 'normal', origin: 'life_radar', data: { radar: item.key } });
  return { action: 'radar.task', entity: 'tasks', entityId: id, summary: title, puts: [put('tasks', task)], deletes: [] };
}
